import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LoadingSpinner from '@/components/ui/loading-spinner';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const PLANS = {
  starter: {
    name: 'Starter',
    price: 29,
    videoLimit: 30,
  },
  pro: {
    name: 'Pro',
    price: 69,
    videoLimit: 100,
  },
};

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const sessionId = searchParams.get('session_id');
  const planId = searchParams.get('plan') || 'starter';
  const plan = PLANS[planId as keyof typeof PLANS] || PLANS.starter;

  useEffect(() => {
    // Simulate verification delay for better UX
    const timer = setTimeout(() => {
      setIsVerifying(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [sessionId]);

  if (isVerifying) {
    return <LoadingSpinner size="lg" text="Confirming your subscription..." fullPage />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" 
      style={{ background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, #f5f5f5 100%)` }}>
      <SEO
        title="Subscription Confirmed"
        description="Your BrandComply subscription is now active. Start launching compliant influencer campaigns."
        path="/subscription/success"
      />
      <Card className="w-full max-w-md border-0 shadow-xl" style={{ background: PALETTE.gunmetal }}>
        <CardContent className="p-8 text-center">
          {/* Brand logo */}
          <div className="mb-6 flex items-center justify-center">
            <img
              src="/brandcomply_logo_full.png"
              alt="BrandComply"
              className="h-8 w-auto"
            />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold mb-2" style={{ color: PALETTE.americanSilver }}>
            Welcome to BrandComply! 
          </h1>
          <p className="text-base mb-6" style={{ color: PALETTE.americanSilver }}>
            Your {plan.name} Plan is now active. We review up to {plan.videoLimit} videos per month for you.
          </p>

          {/* What's Next */}
          <div className="text-left p-4 rounded-lg mb-6" style={{ background: `rgba(209, 209, 209, 0.1)` }}>
            <h3 className="font-semibold mb-3" style={{ color: PALETTE.americanSilver }}>
              What's next?
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: PALETTE.americanSilver }}>
              <li className="flex items-start gap-2">
                <span style={{ color: PALETTE.inchworm }}>✓</span>
                Create your first campaign
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: PALETTE.inchworm }}>✓</span>
                Upload your campaign briefing
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: PALETTE.inchworm }}>✓</span>
                Share the smart submission link with creators
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: PALETTE.inchworm }}>✓</span>
                Review submissions
              </li>
            </ul>
          </div>

          {/* CTA Button */}
          <Button
            onClick={() => navigate('/brand/campaign')}
            className="w-full h-12 rounded-full text-base font-semibold"
            style={{ 
              background: PALETTE.inchworm, 
              color: PALETTE.gunmetal 
            }}
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>          
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;

